// src/pages/EmailMarketing.jsx
// Single-page email-marketing console. Tabbed layout — each tab is a small
// editor over /api/email-marketing.
//
// Tabs:
//   1. Configuration   — Resend API key + from address + verify test
//   2. Contacts        — list, add, paste-import (one email per line)
//   3. Segments        — rule builder over email_contacts
//   4. Templates       — subject + HTML body editor
//   5. Campaigns       — broadcast (template + segment + schedule) and series
//                        (multiple template steps with delays)
//   6. Activity        — send log
//
// Builders are intentionally minimal — the goal of this PR is a working
// foundation. Richer editors (drag-and-drop, advanced segment ops) can layer
// on top of the same API without schema changes.

import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../lib/authFetch';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
  Button, Input, Badge, Alert, AlertDescription,
  Tabs, TabsList, TabsTrigger, TabsContent,
  RichTextEditor,
} from '../components/ui';
import {
  Mail, Send, Users, Filter, FileText, Megaphone, Activity,
  Plus, Trash2, Edit3, Calendar, Check, RefreshCw, AlertCircle, Inbox,
  UserPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';

const API = '/api/email-marketing';

// User-friendly label for a series trigger_type — used in status messages.
function triggerLabel(t) {
  switch (t) {
    case 'signup':                 return 'user-signup';
    case 'subscription_started':   return 'new-subscription';
    case 'subscription_canceled':  return 'subscription-cancel';
    case 'tag_added':              return 'tag-added';
    case 'date':                   return 'date-based';
    default:                       return 'event';
  }
}

async function api(action, body = null, method = 'GET') {
  const url = method === 'GET'
    ? `${API}?action=${action}${body ? '&' + new URLSearchParams(body).toString() : ''}`
    : `${API}?action=${action}`;
  const res = await authFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: method === 'GET' ? undefined : JSON.stringify({ ...body, action }),
  });
  return res.json();
}

export default function EmailMarketing() {
  const { user } = useAuth();
  const [tab, setTab] = useState('config');

  if (!user) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Email Marketing</h1>
        <p className="text-muted-foreground">
          Send broadcasts and follow-up series to your own audience using your own provider.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="config"><Mail className="h-4 w-4 mr-1.5" />Configuration</TabsTrigger>
          <TabsTrigger value="contacts"><Users className="h-4 w-4 mr-1.5" />Contacts</TabsTrigger>
          <TabsTrigger value="segments"><Filter className="h-4 w-4 mr-1.5" />Segments</TabsTrigger>
          <TabsTrigger value="templates"><FileText className="h-4 w-4 mr-1.5" />Templates</TabsTrigger>
          <TabsTrigger value="campaigns"><Megaphone className="h-4 w-4 mr-1.5" />Campaigns</TabsTrigger>
          <TabsTrigger value="activity"><Activity className="h-4 w-4 mr-1.5" />Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="config">    <ConfigTab />    </TabsContent>
        <TabsContent value="contacts">  <ContactsTab />  </TabsContent>
        <TabsContent value="segments">  <SegmentsTab />  </TabsContent>
        <TabsContent value="templates"> <TemplatesTab /> </TabsContent>
        <TabsContent value="campaigns"> <CampaignsTab /> </TabsContent>
        <TabsContent value="activity">  <ActivityTab />  </TabsContent>
      </Tabs>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// CONFIGURATION
// ──────────────────────────────────────────────────────────────────────────────
function ConfigTab() {
  const [config, setConfig] = useState({
    provider: 'resend', from_name: '', from_email: '', reply_to_email: '', api_key: '',
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    (async () => {
      const { config: existing } = await api('get_config');
      if (existing) setConfig(c => ({ ...c, ...existing }));
      setLoaded(true);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const { success, error } = await api('save_config', config, 'POST');
    setSaving(false);
    if (success) toast.success('Configuration saved');
    else toast.error(error || 'Failed to save');
  };

  const verify = async () => {
    setVerifying(true);
    const tid = toast.loading('Sending test email…');
    const { success, error } = await api('verify_config', { to: testTo || config.from_email }, 'POST');
    setVerifying(false);
    if (success) {
      toast.success('Test email sent — check inbox', { id: tid });
      setConfig(c => ({ ...c, verified: true }));
    } else {
      toast.error(error || 'Verification failed', { id: tid });
    }
  };

  if (!loaded) return <Skeleton />;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" /> Provider Configuration
        </CardTitle>
        <CardDescription>
          Set up the email service that will actually deliver your emails. Resend is recommended for simplicity.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium">Provider</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {[
              { key: 'resend',   label: 'Resend',  ready: true },
              { key: 'smtp',     label: 'SMTP',    ready: false },
              { key: 'sendgrid', label: 'SendGrid',ready: false },
              { key: 'ses',      label: 'AWS SES', ready: false },
            ].map(p => (
              <button key={p.key}
                onClick={() => p.ready && setConfig(c => ({ ...c, provider: p.key }))}
                disabled={!p.ready}
                className={`px-3 py-2 rounded-lg text-sm border transition-all
                  ${config.provider === p.key ? 'border-primary bg-primary/10 text-primary' : 'border-border'}
                  ${!p.ready ? 'opacity-40 cursor-not-allowed' : 'hover:border-primary/60'}`}
              >
                {p.label} {!p.ready && <span className="text-xs">(coming soon)</span>}
              </button>
            ))}
          </div>
        </div>

        {config.provider === 'resend' && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Get a free API key at <a className="underline" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com/api-keys</a>.
              Verify a sending domain before going live.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">From name</label>
            <Input value={config.from_name || ''} onChange={e => setConfig(c => ({ ...c, from_name: e.target.value }))} placeholder="Your Brand" />
          </div>
          <div>
            <label className="text-sm font-medium">From email</label>
            <Input value={config.from_email || ''} onChange={e => setConfig(c => ({ ...c, from_email: e.target.value }))} placeholder="hello@yourdomain.com" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm font-medium">Reply-to (optional)</label>
            <Input value={config.reply_to_email || ''} onChange={e => setConfig(c => ({ ...c, reply_to_email: e.target.value }))} placeholder="support@yourdomain.com" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm font-medium">API key</label>
            <Input type="password" value={config.api_key || ''} onChange={e => setConfig(c => ({ ...c, api_key: e.target.value }))} placeholder="re_xxxxxxxx" />
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
          <div>
            {config.verified
              ? <Badge className="bg-green-500"><Check className="h-3 w-3 mr-1" />Verified</Badge>
              : <Badge variant="outline">Not yet verified</Badge>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Input className="w-48" placeholder="test@yourdomain.com" value={testTo} onChange={e => setTestTo(e.target.value)} />
            <Button variant="outline" onClick={verify} disabled={verifying || !config.api_key || !config.from_email}>
              <Send className="h-4 w-4 mr-1.5" />Send test
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// CONTACTS
// ──────────────────────────────────────────────────────────────────────────────
function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', tags: '' });
  const [bulk, setBulk] = useState('');
  const [syncing, setSyncing] = useState(false);
  const autoSyncedRef = useRef(false);

  const load = useCallback(async () => {
    const { contacts, total } = await api('list_contacts', { search });
    setContacts(contacts || []);
    setTotal(total || 0);
    return total || 0;
  }, [search]);

  const syncUsers = useCallback(async (silent = false) => {
    if (syncing) return;
    setSyncing(true);
    const tid = silent ? null : toast.loading('Syncing RevHapi users…');
    const { success, synced, error } = await api('sync_users', {}, 'POST');
    setSyncing(false);
    if (success) {
      if (!silent) toast.success(`Synced ${synced} users`, { id: tid });
      load();
    } else {
      if (!silent) toast.error(error || 'Sync failed', { id: tid });
    }
  }, [syncing, load]);

  // On first mount: if the list is empty AND we haven't already auto-synced
  // this session, pull in all RevHapi users so the admin sees their audience
  // without having to click anything.
  useEffect(() => {
    (async () => {
      const count = await load();
      if (count === 0 && !autoSyncedRef.current) {
        autoSyncedRef.current = true;
        syncUsers(true);  // silent — no toast
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.email) return toast.error('Email required');
    const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
    const { success, error } = await api('save_contact', { ...form, tags }, 'POST');
    if (success) {
      toast.success('Contact added');
      setForm({ email: '', first_name: '', last_name: '', tags: '' });
      setShowAdd(false);
      load();
    } else toast.error(error || 'Failed');
  };

  const importBulk = async () => {
    const lines = bulk.split('\n').map(l => l.trim()).filter(Boolean);
    const contacts = lines.map(line => {
      // Accept "email" or "email, first, last"
      const parts = line.split(',').map(p => p.trim());
      return { email: parts[0], first_name: parts[1] || null, last_name: parts[2] || null };
    });
    const { success, imported, error } = await api('import_contacts', { contacts }, 'POST');
    if (success) {
      toast.success(`Imported ${imported} contacts`);
      setBulk(''); setShowImport(false); load();
    } else toast.error(error || 'Failed');
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this contact?')) return;
    await api('delete_contact', { id }, 'POST');
    load();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Contacts</CardTitle>
            <CardDescription>{total.toLocaleString()} total · subscribed audience</CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Input className="w-48" placeholder="Search email…" value={search} onChange={e => setSearch(e.target.value)} />
            <Button variant="outline" onClick={() => syncUsers(false)} disabled={syncing}>
              {syncing
                ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                : <UserPlus className="h-4 w-4 mr-1.5" />}
              Sync RevHapi users
            </Button>
            <Button variant="outline" onClick={() => setShowImport(s => !s)}><Inbox className="h-4 w-4 mr-1.5" />Import</Button>
            <Button onClick={() => setShowAdd(s => !s)}><Plus className="h-4 w-4 mr-1.5" />Add</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">

        {showAdd && (
          <div className="border border-border rounded-lg p-4 grid sm:grid-cols-2 gap-3">
            <Input placeholder="email@domain.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <Input placeholder="First name" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
            <Input placeholder="Last name"  value={form.last_name}  onChange={e => setForm({ ...form, last_name: e.target.value })} />
            <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={add}>Save</Button>
            </div>
          </div>
        )}

        {showImport && (
          <div className="border border-border rounded-lg p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Paste one contact per line. Format: <code>email</code> or <code>email, first_name, last_name</code>.
            </p>
            <textarea
              value={bulk} onChange={e => setBulk(e.target.value)}
              rows={6}
              className="w-full p-3 border border-border rounded-lg text-sm font-mono bg-card text-foreground"
              placeholder={'alice@example.com\nbob@example.com, Bob, Jones'}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowImport(false)}>Cancel</Button>
              <Button onClick={importBulk}>Import</Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>{['Email','Name','Tags','Source','Subscribed',''].map(h => (
                <th key={h} className="px-3 py-2 text-left font-medium text-muted-foreground">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-border">
              {contacts.map(c => (
                <tr key={c.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium">{c.email}</td>
                  <td className="px-3 py-2">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-3 py-2">{(c.tags || []).map(t => <Badge key={t} className="mr-1">{t}</Badge>)}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{c.source}</td>
                  <td className="px-3 py-2">{c.subscribed ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button className="p-1 hover:bg-muted rounded" onClick={() => remove(c.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted-foreground py-12">No contacts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// SEGMENTS — small rule builder
// ──────────────────────────────────────────────────────────────────────────────
const FIELDS = [
  { value: 'email',        label: 'Email' },
  { value: 'first_name',   label: 'First name' },
  { value: 'last_name',    label: 'Last name' },
  { value: 'tags',         label: 'Tags' },
  { value: 'custom.company', label: 'Company (custom)' },
  { value: 'custom.plan',    label: 'Plan (custom)' },
];
const OPS = [
  { value: 'equals',       label: 'equals' },
  { value: 'not_equals',   label: 'does not equal' },
  { value: 'contains',     label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'starts_with',  label: 'starts with' },
  { value: 'ends_with',    label: 'ends with' },
  { value: 'is_empty',     label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

function SegmentsTab() {
  const [segments, setSegments] = useState([]);
  const [editing, setEditing] = useState(null); // { id?, name, description, rules }
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const { segments } = await api('list_segments');
    setSegments(segments || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const blank = () => ({ name: '', description: '', rules: { match: 'all', conditions: [{ field: 'email', op: 'ends_with', value: '' }] } });

  const save = async () => {
    if (!editing.name) return toast.error('Name required');
    const { success, error } = await api('save_segment', editing, 'POST');
    if (success) { toast.success('Segment saved'); setEditing(null); load(); }
    else toast.error(error || 'Failed');
  };

  const previewMatch = async () => {
    const { count, sample } = await api('preview_segment', { rules: editing.rules }, 'POST');
    setPreview({ count, sample });
  };

  const updateCondition = (i, patch) => {
    setEditing(e => ({
      ...e,
      rules: { ...e.rules, conditions: e.rules.conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c) },
    }));
  };
  const addCondition = () => setEditing(e => ({
    ...e,
    rules: { ...e.rules, conditions: [...e.rules.conditions, { field: 'email', op: 'contains', value: '' }] },
  }));
  const removeCondition = (i) => setEditing(e => ({
    ...e,
    rules: { ...e.rules, conditions: e.rules.conditions.filter((_, idx) => idx !== i) },
  }));

  if (editing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{editing.id ? 'Edit segment' : 'New segment'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input placeholder="Segment name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
          <Input placeholder="Description (optional)" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} />

          <div className="border border-border rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              Match
              <select value={editing.rules.match} onChange={e => setEditing({ ...editing, rules: { ...editing.rules, match: e.target.value } })}
                className="border border-border rounded px-2 py-1 bg-card">
                <option value="all">all</option>
                <option value="any">any</option>
              </select>
              of these conditions:
            </div>

            {editing.rules.conditions.map((c, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-center">
                <select value={c.field} onChange={e => updateCondition(i, { field: e.target.value })}
                  className="border border-border rounded px-2 py-1 bg-card text-sm">
                  {FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select value={c.op} onChange={e => updateCondition(i, { op: e.target.value })}
                  className="border border-border rounded px-2 py-1 bg-card text-sm">
                  {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {!['is_empty','is_not_empty'].includes(c.op) && (
                  <Input className="flex-1 min-w-[120px]" placeholder="Value"
                    value={c.value || ''} onChange={e => updateCondition(i, { value: e.target.value })} />
                )}
                <button className="p-1 hover:bg-muted rounded" onClick={() => removeCondition(i)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>
            ))}

            <Button variant="outline" size="sm" onClick={addCondition}><Plus className="h-3 w-3 mr-1" />Add condition</Button>
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Button variant="outline" onClick={previewMatch}>Preview matches</Button>
            {preview && (
              <span className="text-sm">
                <strong>{preview.count}</strong> contacts match
                {preview.sample.length > 0 && ` · e.g. ${preview.sample.map(s => s.email).join(', ')}`}
              </span>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save}>Save segment</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Filter className="h-5 w-5" />Segments</CardTitle>
            <CardDescription>Group your contacts by customer type, lifecycle, or behaviour.</CardDescription>
          </div>
          <Button onClick={() => setEditing(blank())}><Plus className="h-4 w-4 mr-1.5" />New segment</Button>
        </div>
      </CardHeader>
      <CardContent>
        {segments.length === 0
          ? <p className="text-sm text-muted-foreground py-6 text-center">No segments yet. Create one to target campaigns.</p>
          : (
            <ul className="divide-y divide-border">
              {segments.map(s => (
                <li key={s.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.description || '—'} · {s.rules?.conditions?.length || 0} conditions</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(s)}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      if (window.confirm(`Delete "${s.name}"?`)) { await api('delete_segment', { id: s.id }, 'POST'); load(); }
                    }}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// TEMPLATES
// ──────────────────────────────────────────────────────────────────────────────
function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [testTo, setTestTo] = useState('');

  const load = useCallback(async () => {
    const { templates } = await api('list_templates');
    setTemplates(templates || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.subject) return toast.error('Subject required');
    const { success, error, template } = await api('save_template', editing, 'POST');
    if (success) { toast.success('Template saved'); setEditing(template); load(); }
    else toast.error(error || 'Failed');
  };

  const sendTest = async () => {
    if (!testTo) return toast.error('Enter a test recipient');
    const { success, error } = await api('send_test', { template_id: editing.id, to: testTo }, 'POST');
    if (success) toast.success('Test sent');
    else toast.error(error || 'Failed');
  };

  if (editing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{editing.id ? 'Edit template' : 'New template'}</CardTitle>
          <CardDescription>Merge tags: {'{{first_name}} {{last_name}} {{email}} {{custom.field}}'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input placeholder="Template name (internal)" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
          <Input placeholder="Subject line" value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })} />
          <Input placeholder="Preview text (preheader, optional)" value={editing.preview_text || ''} onChange={e => setEditing({ ...editing, preview_text: e.target.value })} />
          <RichTextEditor
            value={editing.body_html || ''}
            onChange={(html) => setEditing({ ...editing, body_html: html })}
            height={320}
            placeholder="Hi {{first_name}}, your message here…"
          />
          <p className="text-xs text-muted-foreground">
            The RevHapi logo, unsubscribe link, and contact email are added automatically when the email is sent. You only need to write the body.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            {editing.id && (
              <>
                <Input className="w-48" placeholder="test@you.com" value={testTo} onChange={e => setTestTo(e.target.value)} />
                <Button variant="outline" onClick={sendTest}><Send className="h-4 w-4 mr-1.5" />Send test</Button>
              </>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setEditing(null)}>Done</Button>
            <Button onClick={save}>Save</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Templates</CardTitle>
            <CardDescription>Reusable subject + HTML body.</CardDescription>
          </div>
          <Button onClick={() => setEditing({ name: 'Untitled', subject: '', body_html: '' })}>
            <Plus className="h-4 w-4 mr-1.5" />New template
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {templates.length === 0
          ? <p className="text-sm text-muted-foreground py-6 text-center">No templates yet.</p>
          : (
            <ul className="divide-y divide-border">
              {templates.map(t => (
                <li key={t.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.subject || '(no subject)'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(t)}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      if (window.confirm(`Delete "${t.name}"?`)) { await api('delete_template', { id: t.id }, 'POST'); load(); }
                    }}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// CAMPAIGNS (broadcast + series)
// ──────────────────────────────────────────────────────────────────────────────
function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingSteps, setEditingSteps] = useState([]);

  const load = useCallback(async () => {
    const [{ campaigns }, { segments }, { templates }] = await Promise.all([
      api('list_campaigns'), api('list_segments'), api('list_templates'),
    ]);
    setCampaigns(campaigns || []);
    setSegments(segments || []);
    setTemplates(templates || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = (type) => {
    setEditing({
      name: 'Untitled campaign',
      type,
      status: 'draft',
      segment_id: null,
      template_id: null,
      scheduled_at: null,
      trigger_type: type === 'series' ? 'manual' : 'manual',
    });
    setEditingSteps([]);
  };

  // Toggle a series between 'running' (listening for triggers) and 'paused'.
  const toggleActive = async () => {
    const next = editing.status === 'running' ? 'paused' : 'running';
    const { success, error, campaign } = await api('save_campaign', { ...editing, status: next }, 'POST');
    if (success) {
      setEditing(campaign);
      toast.success(next === 'running' ? 'Series is now listening for triggers' : 'Series paused');
      load();
    } else toast.error(error || 'Failed');
  };

  const openExisting = async (c) => {
    const { campaign, steps } = await api('get_campaign', { id: c.id }, 'POST');
    setEditing(campaign);
    setEditingSteps((steps || []).map(s => ({
      template_id: s.template_id, delay_amount: s.delay_amount, delay_unit: s.delay_unit,
    })));
  };

  const save = async () => {
    if (!editing.name) return toast.error('Name required');
    const { success, error, campaign } = await api('save_campaign', editing, 'POST');
    if (!success) return toast.error(error);
    if (editing.type === 'series') {
      await api('save_campaign_steps', { campaign_id: campaign.id, steps: editingSteps }, 'POST');
    }
    toast.success('Campaign saved');
    setEditing(null); setEditingSteps([]); load();
  };

  const schedule = async () => {
    if (!editing.scheduled_at) return toast.error('Pick a date/time');
    const { success, error } = await api('schedule_campaign', { id: editing.id, scheduled_at: editing.scheduled_at }, 'POST');
    if (success) { toast.success('Scheduled — cron will pick it up'); setEditing(null); load(); }
    else toast.error(error || 'Failed');
  };

  const enroll = async () => {
    if (!editing.segment_id) return toast.error('Pick a segment first');
    const { success, error, enrolled } = await api('enroll_segment', { campaign_id: editing.id, segment_id: editing.segment_id }, 'POST');
    if (success) toast.success(`Enrolled ${enrolled} contacts`);
    else toast.error(error || 'Failed');
  };

  if (editing) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{editing.type === 'broadcast' ? 'Broadcast' : 'Follow-up series'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input placeholder="Campaign name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />

            {/* Trigger picker for series only. Broadcasts always go to a segment. */}
            {editing.type === 'series' && (
              <div>
                <label className="text-sm font-medium">Trigger</label>
                <select
                  value={editing.trigger_type || 'manual'}
                  onChange={e => setEditing({ ...editing, trigger_type: e.target.value })}
                  className="w-full mt-1 border border-border rounded px-3 py-2 bg-card"
                >
                  <option value="manual">Manual — enrol a segment myself</option>
                  <option value="signup">When a new user signs up</option>
                  <option value="subscription_started">When a user starts a paid subscription</option>
                  <option value="subscription_canceled">When a user cancels their subscription</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {editing.trigger_type === 'manual'
                    ? 'You choose when to enrol a segment. Series can also be enrolled programmatically.'
                    : 'New enrolments happen automatically when the event fires. No segment needed — every affected user is enrolled.'}
                </p>
              </div>
            )}

            {/* Segment picker — required for broadcasts and manual-trigger series. */}
            {(editing.type === 'broadcast' || (editing.trigger_type || 'manual') === 'manual') && (
              <div>
                <label className="text-sm font-medium">
                  Segment {editing.type === 'series' && <span className="text-xs text-muted-foreground font-normal">(used by the "Enrol segment" button)</span>}
                </label>
                <select value={editing.segment_id || ''} onChange={e => setEditing({ ...editing, segment_id: e.target.value || null })}
                  className="w-full mt-1 border border-border rounded px-3 py-2 bg-card">
                  <option value="">— pick one —</option>
                  {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            {editing.type === 'broadcast' && (
              <>
                <div>
                  <label className="text-sm font-medium">Template</label>
                  <select value={editing.template_id || ''} onChange={e => setEditing({ ...editing, template_id: e.target.value || null })}
                    className="w-full mt-1 border border-border rounded px-3 py-2 bg-card">
                    <option value="">— pick one —</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name} — {t.subject}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Schedule (UTC)</label>
                  <input type="datetime-local" value={editing.scheduled_at ? editing.scheduled_at.slice(0,16) : ''}
                    onChange={e => setEditing({ ...editing, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    className="w-full mt-1 border border-border rounded px-3 py-2 bg-card" />
                </div>
              </>
            )}

            {editing.type === 'series' && (
              <div className="border border-border rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Steps</p>
                {editingSteps.map((s, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Step {i + 1} — after</span>
                    <Input type="number" className="w-20" value={s.delay_amount} onChange={e => {
                      const v = parseInt(e.target.value) || 0;
                      setEditingSteps(steps => steps.map((x, idx) => idx === i ? { ...x, delay_amount: v } : x));
                    }} />
                    <select value={s.delay_unit} onChange={e => {
                      const v = e.target.value;
                      setEditingSteps(steps => steps.map((x, idx) => idx === i ? { ...x, delay_unit: v } : x));
                    }} className="border border-border rounded px-2 py-1 bg-card text-sm">
                      {['minutes','hours','days','weeks'].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <span className="text-muted-foreground">send</span>
                    <select value={s.template_id || ''} onChange={e => {
                      const v = e.target.value;
                      setEditingSteps(steps => steps.map((x, idx) => idx === i ? { ...x, template_id: v } : x));
                    }} className="border border-border rounded px-2 py-1 bg-card text-sm flex-1 min-w-[160px]">
                      <option value="">— template —</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button className="p-1 hover:bg-muted rounded"
                      onClick={() => setEditingSteps(steps => steps.filter((_, idx) => idx !== i))}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => setEditingSteps(s => [...s, { template_id: null, delay_amount: 0, delay_unit: 'days' }])}>
                  <Plus className="h-3 w-3 mr-1" />Add step
                </Button>
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save}>Save</Button>

              {editing.id && editing.type === 'broadcast' && (
                <Button onClick={schedule}><Calendar className="h-4 w-4 mr-1.5" />Schedule</Button>
              )}

              {/* Series: manual triggers get an Enroll-segment action;
                  auto triggers get an Activate / Pause action. */}
              {editing.id && editing.type === 'series' && (editing.trigger_type || 'manual') === 'manual' && (
                <Button onClick={enroll}><Users className="h-4 w-4 mr-1.5" />Enrol segment</Button>
              )}
              {editing.id && editing.type === 'series' && (editing.trigger_type || 'manual') !== 'manual' && (
                <Button
                  onClick={toggleActive}
                  className={editing.status === 'running' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                >
                  {editing.status === 'running'
                    ? <>⏸  Pause</>
                    : <><Check className="h-4 w-4 mr-1.5" />Activate</>}
                </Button>
              )}
            </div>

            {editing.id && editing.type === 'series' && (editing.trigger_type || 'manual') !== 'manual' && (
              <div className={`text-xs rounded-lg p-3 border ${editing.status === 'running' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300' : 'bg-muted/50 border-border text-muted-foreground'}`}>
                {editing.status === 'running'
                  ? <>● Listening — new {triggerLabel(editing.trigger_type)} events will enrol the affected user automatically.</>
                  : <>○ Paused — events are being ignored. Activate to start enrolling.</>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5" />Campaigns</CardTitle>
            <CardDescription>Broadcasts and follow-up series.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openNew('series')}><Plus className="h-4 w-4 mr-1.5" />New series</Button>
            <Button onClick={() => openNew('broadcast')}><Plus className="h-4 w-4 mr-1.5" />New broadcast</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {campaigns.length === 0
          ? <p className="text-sm text-muted-foreground py-6 text-center">No campaigns yet.</p>
          : (
            <ul className="divide-y divide-border">
              {campaigns.map(c => {
                const isAutoSeries = c.type === 'series' && (c.trigger_type || 'manual') !== 'manual';
                const statusColor = c.status === 'sent' ? 'bg-green-500'
                                  : c.status === 'sending' ? 'bg-blue-500'
                                  : c.status === 'scheduled' ? 'bg-amber-500'
                                  : c.status === 'running' ? 'bg-emerald-500'
                                  : '';
                return (
                  <li key={c.id} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium flex items-center gap-2 flex-wrap">
                        {c.name}
                        <Badge variant="outline">{c.type}</Badge>
                        <Badge className={statusColor}>{c.status}</Badge>
                        {isAutoSeries && (
                          <Badge variant="outline" className="text-xs">
                            Trigger: {triggerLabel(c.trigger_type)}
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {c.email_segments?.name && `Segment: ${c.email_segments.name}`}
                        {c.sent_count > 0 && ` · ${c.sent_count} sent`}
                        {c.failed_count > 0 && ` · ${c.failed_count} failed`}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openExisting(c)}><Edit3 className="h-3.5 w-3.5" /></Button>
                  </li>
                );
              })}
            </ul>
          )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY
// ──────────────────────────────────────────────────────────────────────────────
function ActivityTab() {
  const [sends, setSends] = useState([]);
  useEffect(() => {
    (async () => {
      const { sends } = await api('list_sends');
      setSends(sends || []);
    })();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" />Recent activity</CardTitle>
        <CardDescription>Most recent 100 sends.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>{['When','To','Subject','Status','Error'].map(h => (
                <th key={h} className="px-3 py-2 text-left font-medium text-muted-foreground">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sends.map(s => (
                <tr key={s.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(s.sent_at || s.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{s.to_email}</td>
                  <td className="px-3 py-2 truncate max-w-[280px]">{s.subject}</td>
                  <td className="px-3 py-2">
                    <Badge className={s.status === 'sent' ? 'bg-green-500' : s.status === 'failed' ? 'bg-red-500' : ''}>{s.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-red-500 truncate max-w-[260px]">{s.error || ''}</td>
                </tr>
              ))}
              {sends.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted-foreground py-12">No sends yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
function Skeleton() {
  return <div className="h-40 bg-muted/30 rounded-2xl animate-pulse" />;
}
